import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { SSNInput } from '@/components/ui/ssn-input'
import { apiFetch } from '@prescreen/lib/api-client'
import {
  BorrowerBrandingFrame,
  borrowerPageShellClass,
  borrowerSpinnerClass
} from '@/components/borrower/BorrowerBrandingFrame'
import type { PrequalBranding } from '@prescreen/lib/types'

type ConsentPageDto = {
  requestId: string
  branding?: PrequalBranding
  primaryFirstName?: string
  coBorrowerFirstName?: string
  coBorrowerStatus?: string
  needsSsn: boolean
}

export default function CoBorrowerConsentPage() {
  const { requestId } = useParams<{ requestId: string }>()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [dto, setDto] = useState<ConsentPageDto | null>(null)
  const [consent, setConsent] = useState(false)
  const [ssn, setSsn] = useState('')
  const [needsSsn, setNeedsSsn] = useState(false)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    const load = async () => {
      if (!requestId || !token) {
        setError('This consent link is invalid or has expired.')
        setLoading(false)
        return
      }
      try {
        const response = await apiFetch(
          `/api/prequalification/coborrower-consent/${encodeURIComponent(requestId)}?token=${encodeURIComponent(token)}`
        )
        const json = await response.json()
        if (response.status === 403 || response.status === 410) {
          setError('This consent link is invalid or has expired.')
          setLoading(false)
          return
        }
        if (!json.success) {
          setError(json.error || 'Failed to load consent page')
          setLoading(false)
          return
        }
        setDto(json.data as ConsentPageDto)
        setNeedsSsn(Boolean(json.data?.needsSsn))
        setLoading(false)
      } catch {
        setError('Network error. Please try again.')
        setLoading(false)
      }
    }
    void load()
  }, [requestId, token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!requestId || !token) {
      setError('Invalid consent link')
      return
    }
    if (!consent) {
      setError('Consent is required')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const response = await apiFetch('/api/prequalification/coborrower-consent/submit', {
        method: 'POST',
        body: JSON.stringify({
          requestId,
          token,
          consent: true,
          ssn: ssn || undefined
        })
      })
      const json = await response.json()
      if (json.success && json.data?.needsSsn) {
        setNeedsSsn(true)
        setError('Social Security Number is required to complete your credit check.')
        return
      }
      if (!json.success) {
        setError(json.error || 'Submission failed')
        return
      }
      setDone(true)
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="text-center">
          <div
            className={`animate-spin rounded-full h-12 w-12 border-b-2 ${borrowerSpinnerClass(false)} mx-auto mb-4`}
          />
          <p className="text-gray-600">Loading…</p>
        </div>
      </div>
    )
  }

  if (error && !dto) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    )
  }

  const branding = dto?.branding
  const primary = dto?.primaryFirstName || 'the primary applicant'

  if (done) {
    return (
      <BorrowerBrandingFrame branding={branding} className={borrowerPageShellClass(Boolean(branding))}>
        <div className="max-w-xl mx-auto px-4">
          <Card>
            <CardHeader>
              <CardTitle>Thank you</CardTitle>
              <CardDescription>
                Your consent and credit prequalification are complete. You may close this window.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </BorrowerBrandingFrame>
    )
  }

  return (
    <BorrowerBrandingFrame branding={branding} className={borrowerPageShellClass(Boolean(branding))}>
      <div className="max-w-xl mx-auto px-4">
        <Card>
          <CardHeader>
            <CardTitle>Co-borrower consent</CardTitle>
            <CardDescription>
              {dto?.coBorrowerFirstName ? `Hi ${dto.coBorrowerFirstName}, ` : ''}
              {primary} invited you to join their loan prequalification as a co-borrower.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {needsSsn && (
                <div>
                  <Label htmlFor="coConsentSsn">Social Security Number *</Label>
                  <SSNInput
                    id="coConsentSsn"
                    value={ssn}
                    onChange={setSsn}
                    required
                  />
                </div>
              )}
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="coConsent"
                  checked={consent}
                  onCheckedChange={(checked) => setConsent(checked === true)}
                />
                <Label htmlFor="coConsent" className="text-sm leading-snug">
                  I consent to a soft credit prequalification and authorize use of my information for
                  this application.
                </Label>
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Processing…' : 'Submit consent'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </BorrowerBrandingFrame>
  )
}
